import { apiClient, apiOrigin, type ApiResult } from '@/services/apiClient';

const PATH = `${apiOrigin}/api/v1/platform/purge`;

export type PurgeScope = 'TENANT' | 'PLATFORM';

export interface PurgeStatus {
  enabled: boolean;
  eligible: boolean;
  superAdminConfigured: boolean;
  principal: string;
  actorTenantId: string;
  tenants: Array<{ id: string; name: string; slug: string }>;
  history: Array<{ id: string; scope: string; outcome: string; actorEmail: string; targetTenantName: string | null; storageOutcome: string | null; createdAt: string }>;
}

export interface PurgePreview {
  previewId: string;
  scope: PurgeScope;
  keepUsers: boolean;
  expiresAt: string;
  confirmationPhrase: string;
  tenants: Array<{ id: string; name: string; dropped: boolean; counts: Record<string, number>; total: number }>;
  totalRows: number;
  externalRevocations: {
    plaidItems: number;
    accountingConnections: number;
    procoreConnections: number;
    metaConnections: number;
    m365Connections: number;
  };
  objectStorage: { configured: boolean; referencedObjects: number; prefixes: string[] };
  preserved: string[];
}

export interface PurgeResult {
  auditId: string;
  scope: PurgeScope;
  totalRows: number;
  storage: { deleted: number; failed: number };
  revocations: {
    plaid: { revoked: number; failed: string[] };
    accounting: { revoked: number; failed: string[] };
    procore: { revoked: number; failed: string[] };
    other: { revoked: string[]; failed: string[]; warnings: string[] };
  };
}

export function purgeRevocationIssues(result: PurgeResult): string[] {
  return [
    ...result.revocations.plaid.failed.map((item) => `Plaid: ${item}`),
    ...result.revocations.accounting.failed.map((item) => `QuickBooks: ${item}`),
    ...result.revocations.procore.failed.map((item) => `Procore: ${item}`),
    ...result.revocations.other.failed,
    ...result.revocations.other.warnings,
  ];
}

function unwrap<T>(result: ApiResult<T>, fallback: string): T {
  if (!result.ok || result.data === null) throw new Error(result.error ?? fallback);
  return result.data;
}

export const platformPurgeClient = {
  async status(): Promise<PurgeStatus> {
    return unwrap(await apiClient.get<PurgeStatus>(`${PATH}/status`), 'Unable to load purge status');
  },
  async preview(body: { scope: PurgeScope; tenantId?: string; keepUsers: boolean }): Promise<PurgePreview> {
    return unwrap(await apiClient.post<PurgePreview>(`${PATH}/preview`, body), 'Dry run failed');
  },
  async execute(body: { previewId: string; confirmation: string; password: string }): Promise<PurgeResult> {
    return unwrap(await apiClient.post<PurgeResult>(`${PATH}/execute`, body), 'Purge failed');
  },
};
